/**
 * ReviewForm Component
 * Form for submitting a review for a completed job and driver
 */

import React, { useState, useEffect } from 'react';
import StarRating from './StarRating';
import reviewsService from '../../services/reviews';
import './Review.css';

const ReviewForm = ({ job, driver, onReviewSubmitted, existingReview }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (existingReview) {
      setRating(existingReview.rating);
      setComment(existingReview.comment || '');
      setSuccess(true);
    }
  }, [existingReview]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const reviewData = {
        driver: driver._id || driver.id,
        job: job._id || job.id,
        rating,
        comment: comment.trim() || undefined
      };

      const review = await reviewsService.createReview(reviewData);
      setSuccess(true);

      if (onReviewSubmitted) {
        onReviewSubmitted(review);
      }
    } catch (err) {
      setError(err.message || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  // If already reviewed, show the review
  if (success || existingReview) {
    return (
      <div className="review-form-success">
        <div className="review-success-icon">✓</div>
        <h4>Thank you for your review!</h4>
        <div className="review-submitted">
          <StarRating rating={rating || existingReview?.rating} readonly size="large" />
          {(comment || existingReview?.comment) && (
            <p className="review-submitted-comment">"{comment || existingReview?.comment}"</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="review-form-container">
      <div className="review-form-header">
        <h4>Rate your delivery experience</h4>
        <p>How was your experience with the driver?</p>
      </div>

      {driver && (
        <div className="review-driver-info">
          <div className="review-driver-avatar">
            {driver.firstName?.charAt(0)}{driver.lastName?.charAt(0)}
          </div>
          <div className="review-driver-details">
            <span className="review-driver-name">
              {driver.firstName} {driver.lastName}
            </span>
            <span className="review-driver-label">Your Driver</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="review-form">
        <div className="review-form-rating">
          <StarRating
            rating={rating}
            onRatingChange={setRating}
            size="large"
            showLabel
          />
        </div>

        <div className="review-form-comment">
          <label htmlFor="review-comment">Add a comment (optional)</label>
          <textarea
            id="review-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your experience with this delivery..."
            maxLength={1000}
            rows={3}
          />
          <span className="review-char-count">{comment.length}/1000</span>
        </div>

        {error && (
          <div className="review-form-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="review-submit-btn"
          disabled={loading || rating === 0}
        >
          {loading ? 'Submitting...' : 'Submit Review'}
        </button>
      </form>
    </div>
  );
};

export default ReviewForm;
